import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, MessageSquare, Check } from 'lucide-react';
import { Question } from './DataDefinitions';

import { useAuth } from 'providers/AuthProvider'
import { useState } from 'react';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useTranslation } from 'react-i18next';


interface QuestionListProps {
    initialQuestions: Question[];
}


const QuestionList: React.FC<QuestionListProps> = ({ initialQuestions }) => {

    const auth = useAuth();

    const [questions, setQuestions] = useState<Question[]>(initialQuestions);

    const truncateText = (longText: string, maxLength: number = 100) => {
        const truncatedText =
            longText.length > maxLength ? longText.slice(0, maxLength) + '...' : longText;
        return truncatedText;

    };

    const handleVoteUp = async (question_id: number) => {
        const form = new FormData();
        form.append('questionForm', JSON.stringify({ question_id: question_id }));

        try {
            await apiClient.put(
                '/v1/forum/vote/up', form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );

            // Update the votes in the questions array
            setQuestions((prevQuestions) =>
                prevQuestions.map((question) =>
                    question.question_id === question_id
                        ? { ...question, votes: question.votes + 1 }
                        : question
                )
            );
        } catch (error) {
            console.error('Request failed:', error);
        }
    };

    const handleVoteDown = async (question_id: number) => {
        const form = new FormData();
        form.append('questionForm', JSON.stringify({ question_id: question_id }));

        try {
            await apiClient.put(
                '/v1/forum/vote/down', form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            // Update the votes in the questions array
            setQuestions((prevQuestions) =>
                prevQuestions.map((question) =>
                    question.question_id === question_id
                        ? { ...question, votes: question.votes - 1 }
                        : question
                )
            );
        } catch (error) {
            console.error('Request failed:', error);
        }
    };

    // Synchronize localData with data prop
    useEffect(() => {
        setQuestions(initialQuestions); // Update localData whenever data changes
    }, [initialQuestions]); // Dependency array ensures this runs only when data changes

    return (
        <>

            {/* Questions List */}
            <div className="questions-list">
                {questions.map(question => (
                    <div key={question.question_id} className="card mb-3">
                        <div className="card-body">
                            <div className="row">
                                {/* Voting and Stats Column */}
                                <div className="col-md-1 text-center">
                                    <div className="d-flex flex-column align-items-center">
                                        <button className="btn btn-link p-0" onClick={() => handleVoteUp(question.question_id)}>
                                            <ArrowUp className="text-secondary" size={20} />
                                        </button>
                                        <span className="fs-5 my-2">{question.votes}</span>
                                        <button className="btn btn-link p-0" onClick={() => handleVoteDown(question.question_id)}>
                                            <ArrowDown className="text-secondary" size={20} />
                                        </button>
                                    </div>
                                </div>

                                {/* Question Content Column */}
                                <div className="col-md-9">
                                    <div className="d-flex justify-content-between">
                                        <h4 className="card-title mb-3">
                                            {question.solved == 1 && (
                                                <Check className="text-success me-2" size={20} />
                                            )}


                                            <Link to={`/community/forum/question/${question.question_id}`}>
                                                {question.title}
                                            </Link>

                                        </h4>
                                    </div>
                                    <p className="card-text text-secondary fs-7">{truncateText(question.content)}</p>

                                    {/* Tags and Meta Info */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="tags">
                                            {question.tags.map(tag => (
                                                <span key={tag} className="badge bg-light text-secondary me-2">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>

                                    </div>

                                </div>

                                {/* Requester and answeer Column */}
                                <div className="col-md-2 text-center">
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="bg-light p-3 rounded text-secondary small">
                                            asked by {question.author} on {question.inserted_at}
                                        </div>
                                        <div className="mt-3">
                                            <MessageSquare size={20} className="text-secondary" />
                                            <div className="small">{question.answers}</div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default QuestionList;